import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useApiClient } from '@travel/api-client';
import useCurrencyChangeCallback from '@travel/traveler-core/hooks/useCurrencyChangeCallback';
import useDeviceType, {
  DESKTOP_SCREEN,
  MOBILE_SCREEN,
  TABLET_SCREEN,
} from '@travel/traveler-core/hooks/useDeviceType';
import { PROVIDER_LIST_TYPE } from '@travel/traveler-core/types/providerInformation';
import { toSEOSearchString } from '@travel/traveler-core/utils';
import FilterTag from '@travel/ui/components/TagFilter/filterTag';
import { cx, isNotEmptyArray } from '@travel/utils';

import ProviderFilterTags from 'components/ProviderFilterTags';
import SortSelectBox from 'components/SortSelectBox';

import { replaceLocation } from 'store/__router/actions';
import {
  getIsDirectContractType,
  getSelectedNavSection,
} from 'store/providerInformation/selectors';
import { fetchProviderPlanList } from 'store/providerPlanList/actions';
import { fetchPlanList } from 'store/providerPlanList/apis';
import { getFilters as getFiltersPlanList, getPlansTotal } from 'store/providerPlanList/selectors';
import { fetchProviderRatePlan } from 'store/providerRatePlan/actions';
import { fetchAll } from 'store/providerRatePlan/apis';
import {
  getFilters as getFiltersRoomList,
  getItems,
  getPlansTotal as getRoomsTotal,
} from 'store/providerRatePlan/selectors';
import { getCouponDetails } from 'store/salesPromotion/selectors';
import { isClient } from 'store/utils';

import { EMPTY_ARRAY, EMPTY_OBJECT } from 'constants/defaultValue';
import type { SortKeysType } from 'constants/provider';
import { FILTER_GROUP_IDS } from 'constants/provider';
import { TAB_ID } from 'constants/providerInfoTabIds';
import paths from 'core/universalRouter/paths';
import type { PriceRangeType } from 'hooks/useFilterCount';
import useFilterCount from 'hooks/useFilterCount';
import type { ProviderInfoListTypes, ProviderInfoQuery } from 'ProviderInformation-Types';
import type { RatePlanFilterFeature } from 'ProviderRatePlan-Types';

import HorizontalNavScroll from '../../../../components/HorizontalNavScroll';
import { getRatePlanRequestBody } from '../../resolver';
import type { FilterFormProps } from '../HiddenFilterForm';
import type { PlanFilter } from '../RoomList';
import FilterButton from './FilterButton';

import styles from './filters.module.scss';

export type FiltersProps = {
  setMaxPrice: React.Dispatch<React.SetStateAction<PriceRangeType>>;
  setMinPrice: React.Dispatch<React.SetStateAction<PriceRangeType>>;
  setSortKey: React.Dispatch<React.SetStateAction<SortKeysType>>;
  setFreeCancellation: React.Dispatch<React.SetStateAction<boolean>>;
  setFeatures: React.Dispatch<React.SetStateAction<string[]>>;
  onSubmit: () => void;
};

type Props = {
  className?: string;
  /** String to define the current provider id */
  pageIdentifier: string;
  /** Where the search bar is on */
  listType: ProviderInfoListTypes;
  query: ProviderInfoQuery;
  /** Element from parent component that toggles tabs */
  listingTypeSelectorTabList?: JSX.Element;
  tagFilterAndTabListRef?: React.Ref<HTMLDivElement>;
  tabListWidth?: number;
  onRemoveCoupon?: () => void;
  filterValProps: FilterFormProps;
  filtersProps: FiltersProps;
};

const QUICK_FILTER_TAG_TESTID = 'quick-filter-tags';
const MAXIMUM_QUICK_FILTERS = 4;
const PROVIDER_INFO = 'providerInfo';

function Filters(props: Props) {
  const {
    className,
    pageIdentifier,
    tagFilterAndTabListRef,
    tabListWidth = 0,
    query,
    listType,
    listingTypeSelectorTabList,
    onRemoveCoupon,
  } = props;
  const {
    maxPrice,
    minPrice,
    sortKey,
    freeCancellation,
    features,
    searchFeatures,
  } = props.filterValProps;
  const {
    setMaxPrice,
    setMinPrice,
    setSortKey,
    setFreeCancellation,
    setFeatures,
    onSubmit,
  } = props.filtersProps;

  const searchFreeCancellation = query.freeCancellation === 'true';
  const searchedMaxPrice = query.maxPrice ? Number(query.maxPrice) : null;
  const searchedMinPrice = query.minPrice ? Number(query.minPrice) : null;
  const isDirectContractType = useSelector(getIsDirectContractType);
  const apiClient = useApiClient();

  const items = useSelector(getItems);
  const dispatch = useDispatch();
  const deviceType = useDeviceType();
  const isMobile = deviceType === MOBILE_SCREEN;
  const isPC = deviceType === DESKTOP_SCREEN;
  const isTab = deviceType === TABLET_SCREEN;
  const isRoomListType = listType === PROVIDER_LIST_TYPE.ROOM_LIST;
  const params = { pageIdentifier: pageIdentifier };

  const currentCoupon = useSelector(getCouponDetails(query.salesPromotionId || ''));

  const quickFilterWrapperRef = useRef<HTMLDivElement>(null);

  const filterOptionsRoomList = useSelector(getFiltersRoomList);
  const filterOptionsPlanList = useSelector(getFiltersPlanList);
  const initialOptions = isRoomListType ? filterOptionsRoomList : filterOptionsPlanList;
  const recentPriceFilter = initialOptions?.find(
    filter => filter.groupId === FILTER_GROUP_IDS.PRICE,
  );
  const selectedNavSection = useSelector(getSelectedNavSection);

  const [filterOptions, setFilterOptions] = useState(initialOptions);
  const roomListTotal = useSelector(getRoomsTotal);
  const planListTotal = useSelector(getPlansTotal);
  const [isClearFilterButtonDisabled, setIsClearFilterButtonDisabled] = useState<boolean>(false);
  const [total, setTotal] = useState(isRoomListType ? roomListTotal : planListTotal);
  const [quickFilters, setQuickFilters] = useState<RatePlanFilterFeature[]>([]);
  const [filterButtonWidth, setFilterButtonWidth] = useState<number>(0);
  const [quickFiltersThresholdWidth, setQuickFiltersThresholdWidth] = useState<number>(0);
  const [isScrollingDown, setIsScrollingDown] = useState<boolean>(false);
  const [isPricePopupOpen, setPricePopupOpen] = useState(false);
  const [hidePricePopup, setHidePricePopup] = useState(false);
  const prevScrollPos = useRef(0);
  const filterCount = useFilterCount(
    { features, maxPrice, minPrice, freeCancellation },
    filterOptions,
  );
  const isDatedSearch = query?.startDate || query?.endDate;
  const onPage = isDatedSearch ? 'providerInfoDated' : 'providerInfoUndated';

  useEffect(() => {
    if (
      isClearFilterButtonDisabled &&
      ((minPrice && Math.floor(minPrice) !== Math.floor(recentPriceFilter?.min || 0)) ||
        (maxPrice && Math.floor(maxPrice) !== Math.floor(recentPriceFilter?.max || 0)) ||
        features.length > 0 ||
        freeCancellation)
    ) {
      setIsClearFilterButtonDisabled(false);
    }
  }, [isClearFilterButtonDisabled, minPrice, maxPrice, features, freeCancellation]);

  useEffect(() => {
    setTotal(isRoomListType ? roomListTotal : planListTotal);
    setFilterOptions(initialOptions);
    if (isNotEmptyArray(initialOptions)) {
      const getFirstFeature = initialOptions
        .map(item => item.features?.[0] ?? null)
        .filter(feature => feature && feature?.name) as RatePlanFilterFeature[];
      const uniqueFeatureFilters = Array.from(
        new Set(getFirstFeature.map(feature => JSON.stringify(feature))),
      ).map(item => JSON.parse(item)) as RatePlanFilterFeature[];
      const quickFilters = uniqueFeatureFilters.slice(
        0,
        Math.min(uniqueFeatureFilters.length, MAXIMUM_QUICK_FILTERS),
      );
      setQuickFilters(quickFilters);
    } else {
      setQuickFilters([]);
    }
  }, [initialOptions, isRoomListType, planListTotal, roomListTotal]);

  // This function required to toggles the visibility of applied filters based on the scroll direction.
  const updateScrollDirection = (currentScrollPos: number): void => {
    setIsScrollingDown(currentScrollPos > prevScrollPos.current);
    prevScrollPos.current = currentScrollPos;
  };

  const handleScroll = useCallback(() => {
    if (!isClient) {
      return;
    }
    const currentScrollPos = window.scrollY;
    if (selectedNavSection?.id === TAB_ID.ROOMS_AND_PLANS && isPC) {
      updateScrollDirection(currentScrollPos);
    }
  }, [isPC, selectedNavSection?.id]);

  useEffect(() => {
    if (!isClient) {
      return;
    }
    prevScrollPos.current = window.scrollY;
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, isScrollingDown]);

  // Calculate the width of the quick filter wrapper to display it between the filter button and tab list
  useEffect(() => {
    if (quickFilterWrapperRef.current && isClient) {
      const computedStyle = window.getComputedStyle(quickFilterWrapperRef.current);
      const marginLeft = parseInt(computedStyle.marginLeft || '0', 10);
      const marginRight = parseInt(computedStyle.marginRight || '0', 10);
      setQuickFiltersThresholdWidth(filterButtonWidth + tabListWidth - (marginLeft + marginRight)); // Subtracting the margin of quickFilterWrapper
    }
  }, [filterButtonWidth, tabListWidth, quickFilterWrapperRef, quickFilters]);

  const toggleFilterOptions = useCallback(
    (options: PlanFilter) => {
      let nextFeatures = [...features];

      if (isNotEmptyArray(options.features)) {
        const filteredFeatures = features.filter(feature => !options.features?.includes(feature));
        const newlyAddedFeatures = options.features.filter(feature => !features?.includes(feature));

        nextFeatures = [...filteredFeatures, ...newlyAddedFeatures];
      }

      const nextFilterOptions = {
        maxPrice: options.maxPrice,
        minPrice: options.minPrice,
        freeCancellation: options.freeCancellation,
        features: nextFeatures,
      };

      setFeatures(nextFilterOptions.features);
      setMaxPrice(nextFilterOptions.maxPrice);
      setMinPrice(nextFilterOptions.minPrice);
      setFreeCancellation(Boolean(nextFilterOptions.freeCancellation));
      return nextFilterOptions;
    },
    [features, setFeatures, setFreeCancellation, setMaxPrice, setMinPrice],
  );

  const onCurrencyChange = useCallback(() => {
    dispatch(
      replaceLocation({
        pathname: paths.providerInfo.pathResolver(params.pageIdentifier),
        search: toSEOSearchString({ ...query, maxPrice: '', minPrice: '' }),
      }),
    );
  }, [query, dispatch, params.pageIdentifier]);

  useCurrencyChangeCallback(onCurrencyChange);

  const getTotalNumber = async (
    options: PlanFilter,
    fetchFilters: boolean = false,
  ): Promise<number> => {
    let total = isRoomListType ? roomListTotal : planListTotal;

    const reqBody = getRatePlanRequestBody(
      pageIdentifier,
      { ...query, ...options, freeCancellation: String(options.freeCancellation) },
      currentCoupon,
    );

    try {
      if (isRoomListType) {
        total = await fetchRoomListTotal(reqBody, fetchFilters);
      } else {
        total = await fetchPlanListTotal(reqBody, fetchFilters);
      }
    } catch (error) {
      // console.error('Error fetching total number:', error);
    }

    return total;
  };

  const fetchRoomListTotal = async (reqBody: any, fetchFilters: boolean): Promise<number> => {
    const response = await fetchAll(apiClient, reqBody);
    let total = response?.items?.length || 0;

    if (roomListTotal === 0 && fetchFilters) {
      await dispatch(fetchProviderRatePlan(reqBody));
      total = roomListTotal;
    }

    return total;
  };

  const fetchPlanListTotal = async (reqBody: any, fetchFilters: boolean): Promise<number> => {
    const response = await fetchPlanList(apiClient, reqBody);
    let total = response?.plans?.length || 0;

    if (planListTotal === 0 && fetchFilters) {
      await dispatch(fetchProviderPlanList(reqBody));
      total = planListTotal;
    }

    return total;
  };

  const updateTotalNumber = async (
    options: PlanFilter,
    fetchFilters: boolean = false,
  ): Promise<void> => {
    const total = await getTotalNumber(options, fetchFilters);
    setTotal(total);
  };

  const onPriceSliderUpdate = (min?: number | null, max?: number | null) => {
    setMaxPrice(max);
    setMinPrice(min);
  };

  const onFreeCancellationUpdate = () => {
    setFreeCancellation(!freeCancellation);
  };

  // execute when every single value is updated
  const onFilterChange = (toggleOptions: PlanFilter) => {
    const nextFilterOptions = toggleFilterOptions({
      features: EMPTY_ARRAY,
      maxPrice,
      minPrice,
      freeCancellation,
      ...toggleOptions,
    });

    updateTotalNumber(nextFilterOptions);
  };

  const onClearFilter = () => {
    setFeatures(EMPTY_ARRAY);
    setMaxPrice(null);
    setMinPrice(null);
    setFreeCancellation(false);

    updateTotalNumber(
      {
        maxPrice: null,
        minPrice: null,
        freeCancellation: false,
        features: EMPTY_ARRAY,
      },
      true,
    );

    setIsClearFilterButtonDisabled(true);
  };

  const onResetFilter = () => {
    // reset filter state to the recent searched one
    setMinPrice(searchedMinPrice);
    setMaxPrice(searchedMaxPrice);
    setFeatures(searchFeatures);
    setFreeCancellation(searchFreeCancellation);
    setTotal(isRoomListType ? roomListTotal : planListTotal);
  };

  // execute when selected tag removed
  const onToggleFilterTag = useCallback(
    (toggleOptions: PlanFilter) => {
      toggleFilterOptions({
        features: EMPTY_ARRAY,
        maxPrice,
        minPrice,
        freeCancellation,
        ...toggleOptions,
      });
    },
    [maxPrice, minPrice, freeCancellation, toggleFilterOptions],
  );

  const onRemovePriceRange = useCallback(() => {
    onToggleFilterTag({ maxPrice: null, minPrice: null });
    setTimeout(() => {
      onSubmit();
    }, 0);
  }, [onToggleFilterTag, onSubmit]);

  const onToggleFeatures = useCallback(
    (ids: Array<string>) => {
      onToggleFilterTag({ features: ids });
      setTimeout(() => {
        onSubmit();
      }, 0);
    },
    [onToggleFilterTag, onSubmit],
  );

  const quickFilterItems = quickFilters.map(filterItem => (
    <FilterTag
      key={filterItem.featureId}
      value={filterItem.name}
      customClass={styles.quickFilterItem}
      isChecked={filterItem.selected}
      selectedTagClass={styles.selectedQuickFilterItem}
      onClick={() => onToggleFeatures([filterItem.featureId])}
      testId={QUICK_FILTER_TAG_TESTID}
    />
  ));

  const onRemoveFreeCancellation = useCallback(() => {
    onToggleFilterTag?.({ freeCancellation: false });
    setTimeout(() => onSubmit?.(), 0);
  }, [onSubmit, onToggleFilterTag]);

  const onChangeSortKey = (filterGroupId: string, toggleOption: string) => {
    setSortKey(toggleOption as SortKeysType);
  };

  const filterButton = (
    <FilterButton
      onPage={onPage}
      listType={listType}
      isClearFilterButtonDisabled={isClearFilterButtonDisabled}
      onClickSearch={onSubmit}
      total={total}
      features={features}
      maxPrice={maxPrice}
      minPrice={minPrice}
      onClear={onClearFilter}
      onReset={onResetFilter}
      onFilterChange={onFilterChange}
      onPriceSliderUpdate={onPriceSliderUpdate}
      filterCount={filterCount}
      filterContentWrapper={styles.filterContentWrapper}
      setFilterButtonWidth={setFilterButtonWidth}
      onChangeSortKey={onChangeSortKey}
      sortKey={sortKey}
      freeCancellation={freeCancellation}
      onFreeCancellationUpdate={onFreeCancellationUpdate}
    />
  );

  // This function toggles visibility (priceSort popup) based on horizontal scrolling within the quick filter container.
  const handleOnScroll = (element: HTMLElement) => {
    element.scrollLeft > 0 ? setHidePricePopup(true) : setHidePricePopup(false);
  };

  return (
    <>
      <div className={cx(className, styles.wrapper)} data-testid="filters-wrapper">
        {/* Room filters and Page Listing view Selector Tabs */}
        <div className={styles.tagFilterAndTabListWrapper} ref={tagFilterAndTabListRef}>
          {filterButton}
          {!isMobile && (
            <div
              className={styles.quickFilterWrapper}
              ref={quickFilterWrapperRef}
              style={{ width: `calc(100% - ${quickFiltersThresholdWidth}px)` }} // Calculate the width to display quick filters between the filter button and tab selector
              data-testid="filters-quickFilter-wrapper"
            >
              <HorizontalNavScroll onScroll={handleOnScroll}>
                {initialOptions.length > 0 && (
                  <SortSelectBox
                    className={styles.sortBox}
                    query={query}
                    params={params}
                    onPage={PROVIDER_INFO}
                    popupWrapperClass={
                      isPricePopupOpen && hidePricePopup
                        ? cx(styles.sortBoxPopup, styles.hideSortBoxPopup)
                        : styles.sortBoxPopup
                    }
                    onAppearanceChanged={isOpen => setPricePopupOpen(isOpen)}
                  />
                )}
                {!isTab && quickFilters.length > 0 && quickFilterItems}
              </HorizontalNavScroll>
            </div>
          )}

          {/* removes tab when there are no rooms to be shown. */}
          {/* checking just rooms since plans can not exist without rooms */}
          {isNotEmptyArray(items) && isDirectContractType && listingTypeSelectorTabList}
        </div>
        {!isMobile && (
          <div className={cx(styles.selectedFiltersWrapper, isScrollingDown && styles.hide)}>
            <ProviderFilterTags
              appliedMin={searchedMinPrice}
              appliedMax={searchedMaxPrice}
              isGroupedFeatures
              filters={initialOptions}
              coupon={currentCoupon}
              onRemovePriceRange={onRemovePriceRange}
              onRemoveFeatures={onToggleFeatures}
              onRemoveCoupon={onRemoveCoupon}
              appliedQuickFilters={quickFilters}
              onRemoveFreeCancellation={onRemoveFreeCancellation}
            />
          </div>
        )}
      </div>
    </>
  );
}

Filters.defaultProps = {
  // make the page able to be accessed without query string
  query: EMPTY_OBJECT,
};

export default Filters;
